// AchievementFeed.js — Live MM2-style achievement ticker for home screen
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabase';

const ICONS = {
  levelup:  { emoji:'⬆️', color:'#8B2BE2', label:'levelled up' },
  streak:   { emoji:'🔥', color:'#FF6B00', label:'is on a streak' },
  chest:    { emoji:'📦', color:'#FF9500', label:'unlocked a chest' },
  quiz:     { emoji:'🏆', color:'#00695C', label:'aced a quiz' },
  card:     { emoji:'🃏', color:'#1565C0', label:'got a card' },
  godly:    { emoji:'🌟', color:'#FFD600', label:'got a GODLY card' },
  legendary:{ emoji:'🟠', color:'#E65100', label:'got a Legendary' },
  epic:     { emoji:'🟣', color:'#7B1FA2', label:'got an Epic card' },
  boss:     { emoji:'👾', color:'#FF2D55', label:'defeated a boss' },
  default:  { emoji:'⭐', color:'#FF9500', label:'did something awesome' },
};

function timeAgo(ts) {
  const d = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (d < 60)   return 'just now';
  if (d < 3600) return `${Math.floor(d/60)}m ago`;
  return `${Math.floor(d/3600)}h ago`;
}

// ─── FLOATING TICKER (auto-scrolling strip) ───────────────
export function AchievementTicker({ userId }) {
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('neevstar_activity_feed')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(30);
      if (data) setItems(data);
    };
    load();
    // Real-time subscription
    const sub = supabase.channel('feed_ticker')
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'neevstar_activity_feed' },
        payload => setItems(prev => [payload.new, ...prev.slice(0,29)]))
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, []);

  // Rotate through items every 4s
  useEffect(() => {
    if (items.length === 0) return;
    setVisible(items[idx % items.length]);
    const id = setInterval(() => {
      setIdx(i => {
        const next = (i+1) % items.length;
        setVisible(items[next]);
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [items]);

  if (!visible) return null;

  const type = visible.activity_type || 'default';
  const icon = ICONS[type] || ICONS.default;
  const isOwn = visible.user_id === userId;

  return (
    <AnimatePresence mode="wait">
      <motion.div key={visible.id || idx}
        initial={{ opacity:0, y:-12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:12 }}
        transition={{ duration:0.35 }}
        style={{ margin:'8px 16px 0', background:'white', borderRadius:14,
          padding:'8px 14px', display:'flex', alignItems:'center', gap:10,
          boxShadow:`0 2px 10px ${icon.color}22`, border:`1.5px solid ${icon.color}33` }}>
        <span style={{ fontSize:22, flexShrink:0 }}>{icon.emoji}</span>
        <p style={{ margin:0, fontSize:12, fontWeight:700, color:'#333', flex:1,
          fontFamily:"'Nunito',sans-serif", overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
          <span style={{ fontWeight:900, color:icon.color }}>{isOwn ? 'You' : (visible.child_name || 'Someone')}</span>
          {' '}{visible.activity_detail || icon.label}
        </p>
        <span style={{ fontSize:10, color:'#AAA', fontWeight:700, flexShrink:0,
          fontFamily:"'Nunito',sans-serif" }}>
          {timeAgo(visible.created_at)}
        </span>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── FULL FEED (on profile/dedicated page) ────────────────
export function AchievementFeedFull({ userId, onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('neevstar_activity_feed')
      .select('*').order('created_at', { ascending:false }).limit(50)
      .then(({ data }) => { setItems(data||[]); setLoading(false); })
      .catch(() => setLoading(false));

    const sub = supabase.channel('feed_full')
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'neevstar_activity_feed' },
        p => setItems(prev => [p.new, ...prev.slice(0,49)]))
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, []);

  return (
    <div style={{ minHeight:'100vh', background:'#F8F9FF', fontFamily:"'Nunito',sans-serif", paddingBottom:80 }}>
      <div style={{ background:'linear-gradient(135deg,#00695C,#1A1A6E)', padding:'16px 16px 20px' }}>
        <button onClick={onBack} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)',
          fontSize:14, fontWeight:800, cursor:'pointer', padding:0, fontFamily:"'Nunito',sans-serif", marginBottom:10 }}>
          ← Back
        </button>
        <h2 style={{ margin:0, fontSize:22, fontWeight:900, color:'white' }}>🏆 Achievement Feed</h2>
        <p style={{ margin:'4px 0 0', fontSize:12, color:'rgba(255,255,255,0.7)', fontWeight:700 }}>
          Live from all NeevStar learners
        </p>
      </div>

      <div style={{ padding:'12px 16px' }}>
        {loading && <p style={{ textAlign:'center', color:'#AAA', fontWeight:700 }}>Loading...</p>}
        {!loading && items.length === 0 && (
          <p style={{ textAlign:'center', color:'#AAA', fontWeight:700, padding:'40px 0' }}>
            No activity yet. Start learning to appear here!
          </p>
        )}
        {items.map((item, i) => {
          const type = item.activity_type || 'default';
          const icon = ICONS[type] || ICONS.default;
          const isOwn = item.user_id === userId;
          return (
            <motion.div key={item.id || i}
              initial={{ opacity:0, x:-20 }} animate={{ opacity:1, x:0 }}
              transition={{ delay:i*0.03 }}
              style={{ background:'white', borderRadius:16, padding:'12px 16px', marginBottom:10,
                display:'flex', alignItems:'center', gap:12,
                boxShadow:`0 2px 8px ${icon.color}18`,
                border:`1.5px solid ${isOwn ? icon.color+'55' : '#F0F0F0'}` }}>
              <div style={{ width:44, height:44, borderRadius:'50%', flexShrink:0,
                background:icon.color+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                {icon.emoji}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ margin:0, fontSize:13, fontWeight:900, color:'#1A1A2E',
                  fontFamily:"'Nunito',sans-serif" }}>
                  {isOwn ? '⭐ You' : (item.child_name || 'A learner')}
                  {isOwn && <span style={{ background:'#00695C', color:'white', borderRadius:6,
                    padding:'1px 6px', fontSize:9, fontWeight:900, marginLeft:6 }}>YOU</span>}
                </p>
                <p style={{ margin:'2px 0 0', fontSize:12, color:'#555', fontWeight:700,
                  fontFamily:"'Nunito',sans-serif", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {item.activity_detail}
                </p>
              </div>
              <span style={{ fontSize:11, color:'#AAA', fontWeight:700, flexShrink:0,
                fontFamily:"'Nunito',sans-serif" }}>
                {timeAgo(item.created_at)}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
